import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClinet from "../../api/Api.js";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit"; // Import EditIcon
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";

// Function to fetch remarks reasons from the backend
const fetchRemarks = async () => {
  const response = await apiClinet.get(
    `${process.env.REACT_APP_NEW_DATA}/getRemarks`
  );
  return response.data.remarks;
};

// Function to add a remarks reason to the backend
const addRemarksReason = async (newReason) => {
  const response = await apiClinet.post(
    `${process.env.REACT_APP_NEW_DATA}/addremark`,
    { name: newReason }
  );
};

// Function to delete a remark reason from the backend
const deleteRemark = async (remarks_id) => {
  const response = await apiClinet.delete(
    `${process.env.REACT_APP_NEW_DATA}/deleteremark/${remarks_id}`
  );
};

// Function to edit a remark reason on the backend
const editRemark = async ({ remarkId, updatedReason }) => {
  const remarks_id = remarkId;
  const response = await apiClinet.put(
    `${process.env.REACT_APP_NEW_DATA}/updateremark/${remarks_id}`,
    { name: updatedReason }
  );
};

export default function Remarks() {
  const [newReason, setNewReason] = useState("");
  const [editingRemark, setEditingRemark] = useState(null); // State for editing
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { data, error, isLoading } = useQuery({
    queryKey: ["remarks"],
    queryFn: fetchRemarks,
  });

  const mutationAdd = useMutation({
    mutationFn: addRemarksReason,
    onSuccess: () => {
      queryClient.invalidateQueries(["remarks"]);
      setNewReason("");
      handleClose();
    },
    onError: (error) => {
      console.error("Error adding remarks:", error);
    },
  });

  const mutationDelete = useMutation({
    mutationFn: deleteRemark,
    onSuccess: () => {
      queryClient.invalidateQueries(["remarks"]); // Refresh the remarks list after deletion
    },
    onError: (error) => {
      console.error("Error deleting remark:", error);
    },
  });

  const mutationEdit = useMutation({
    mutationFn: editRemark,
    onSuccess: () => {
      queryClient.invalidateQueries(["remarks"]); // Refresh the remarks list after editing
      setEditingRemark(null);
      setNewReason("");
      handleClose();
    },
    onError: (error) => {
      console.error("Error editing remark:", error);
    },
  });

  const handleAddRemarks = () => {
    if (newReason.trim()) {
      mutationAdd.mutate(newReason);
    }
  };

  const handleEditRemarks = () => {
    if (newReason.trim()) {
      mutationEdit.mutate({
        remarkId: editingRemark.remarks_id,
        updatedReason: newReason,
      });
    }
  };

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setEditingRemark(null); // Reset editing state when closing the dialog
  };

  // Function to handle the delete action
  const handleDelete = (remarkId) => () => {
    if (window.confirm("Are you sure you want to delete this remark?")) {
      mutationDelete.mutate(remarkId);
    }
  };

  // Function to handle the edit action
  const handleEdit = (remark) => () => {
    setEditingRemark(remark);
    setNewReason(remark.name); // Pre-fill the dialog with the current remark data
    handleClickOpen();
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography>Error: {error.message}</Typography>;

  return (
    <Box p={isMobile ? 2 : 4}>
      {/* Title and Button Row */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexDirection={isMobile ? "column" : "row"}
        mb={isMobile ? 2 : 4}
      >
        <Typography
          variant={isMobile ? "h5" : "h4"}
          gutterBottom
          sx={{ textAlign: isMobile ? "center" : "left" }}
        >
          Remarks
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleClickOpen}
          sx={{ mt: isMobile ? 2 : 0 }}
        >
          Add Remarks
        </Button>
      </Box>

      {/* Remarks Table */}
      <TableContainer
        component={Paper}
        sx={{
          overflowX: "auto",
          maxHeight: isMobile ? "70vh" : "auto",
        }}
      >
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              <TableCell>Remarks ID</TableCell>
              <TableCell>Remarks Reason</TableCell>
              <TableCell>Edit</TableCell>
              <TableCell>Delete</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 ? (
              data.map((remark) => (
                <TableRow key={remark.remarks_id}>
                  <TableCell>{remark.remarks_id}</TableCell>
                  <TableCell>{remark.name}</TableCell>
                  <TableCell>
                    <IconButton onClick={handleEdit(remark)} color="primary">
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={handleDelete(remark.remarks_id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog for Adding or Editing a Remark */}
      <Dialog fullScreen={isMobile} open={open} onClose={handleClose}>
        <DialogTitle>
          {editingRemark ? "Edit Remarks" : "Add Remarks"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Remarks"
            type="text"
            fullWidth
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={editingRemark ? handleEditRemarks : handleAddRemarks}
            color="primary"
          >
            {editingRemark ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
